import { motion } from "framer-motion";
import {
  PiggyBank,
  UtensilsCrossed,
  BookOpen,
  Sun,
  Brain,
  Lock,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { PROGRAMS } from "../../data";

const ICONS = { PiggyBank, UtensilsCrossed, BookOpen, Sun, Brain };

const ProgramCard = ({ program, wide, delay }) => {
  const Icon = ICONS[program.icon];
  return (
    <Reveal
      delay={delay}
      className={`${wide ? "lg:col-span-8" : "lg:col-span-4"} group`}
    >
      <div
        data-testid={`program-card-${program.id}`}
        className="relative h-full overflow-hidden rounded-3xl bg-white border border-black/5 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.06)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_40px_70px_-30px_rgba(27,42,74,0.35)]"
      >
        {/* accent bar */}
        <div
          className="h-1.5 w-full"
          style={{ backgroundColor: program.accent }}
        />

        {program.featured && (
          <div className="absolute right-5 top-8 z-20 rotate-3">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[0.7rem] font-bold uppercase tracking-widest text-white shadow-lg"
              style={{ backgroundColor: program.accent }}
            >
              <Lock size={12} /> Coming Soon
            </span>
          </div>
        )}

        <div className={`grid ${wide ? "md:grid-cols-2" : "grid-cols-1"}`}>
          {/* image */}
          <div className="relative overflow-hidden h-52 md:h-full min-h-[13rem]">
            <img
              src={program.image}
              alt={program.imageAlt}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
            <div
              className="absolute inset-0 opacity-30 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-20"
              style={{ backgroundColor: program.accent }}
            />
          </div>

          {/* body */}
          <div className={`p-7 md:p-9 flex flex-col ${wide ? "justify-center" : ""}`}>
            <div className="flex items-center gap-4 mb-5">
              <div
                className="flex h-12 w-12 items-center justify-center rounded-2xl text-white shrink-0"
                style={{ backgroundColor: program.accent }}
              >
                <Icon size={24} strokeWidth={2.2} />
              </div>
              <span className="font-mono text-sm text-charcoal/40">
                {program.index}
              </span>
            </div>

            <h3 className="font-display font-bold tracking-tight text-charcoal text-xl md:text-2xl leading-snug">
              {program.title}
            </h3>
            <p className={`mt-3 text-charcoal/65 leading-relaxed text-[0.975rem] ${wide ? "" : "flex-1"}`}>
              {program.description}
            </p>

            <div className="mt-7">
              <button
                data-testid={`program-btn-${program.id}`}
                disabled
                aria-disabled="true"
                className="inline-flex items-center gap-2 rounded-full border-2 border-dashed px-6 py-2.5 text-sm font-semibold cursor-not-allowed select-none"
                style={{ color: program.accent, borderColor: `${program.accent}55` }}
              >
                <Lock size={14} />
                Coming Soon
              </button>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export const Programs = () => {
  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="relative bg-warmgray py-24 md:py-32 grain"
    >
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="max-w-3xl mb-16 md:mb-20">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-orange mb-5 flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-brand-orange" />
              Our Programs
            </p>
            <h2 className="font-display font-black tracking-tighter text-charcoal text-4xl md:text-6xl leading-[0.95]">
              Five ways we turn
              <br />
              <span className="text-brand-orange">care into action.</span>
            </h2>
            <p className="mt-6 text-lg text-charcoal/65 leading-relaxed">
              Each program tackles a real challenge facing our neighbors — from
              the grocery bill to the classroom to recovery.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <ProgramCard program={PROGRAMS[0]} wide delay={0} />
          <ProgramCard program={PROGRAMS[1]} delay={0.05} />
          <ProgramCard program={PROGRAMS[2]} delay={0.1} />
          <ProgramCard program={PROGRAMS[3]} delay={0.15} />
          <ProgramCard program={PROGRAMS[4]} delay={0.2} />
        </div>
      </div>
    </section>
  );
};
